import {
  Close as CloseIcon,
  Send as SendIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Fab,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
  Zoom,
} from '@mui/material';
import { useState } from 'react';

const WHATSAPP_NUMBER = '996555123456'; // Замените на реальный номер
const DEFAULT_MESSAGE = 'Здравствуйте! Пишу с сайта WASSER. ';

const WhatsAppButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState(DEFAULT_MESSAGE);

  const handleWhatsAppClick = () => {
    if (message.trim()) {
      const encodedMessage = encodeURIComponent(message);
      window.open(
        `https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`,
        '_blank'
      );
      setIsOpen(false);
      setMessage(DEFAULT_MESSAGE);
    }
  };

  const handleQuickContact = () => {
    window.open(
      `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
        DEFAULT_MESSAGE
      )}`,
      '_blank'
    );
  };

  return (
    <>
      {/* Chat popup */}
      <Zoom in={isOpen}>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: { xs: 206, sm: 100 }, // Above WhatsApp button
            right: { xs: 16, sm: 24 },
            width: { xs: 'calc(100% - 32px)', sm: 320 },
            maxWidth: 320,
            borderRadius: 3,
            overflow: 'hidden',
            zIndex: 1299,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              bgcolor: '#25D366',
              color: 'white',
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <WhatsAppIcon />
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  WASSER Support
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  Обычно отвечаем за 5 минут
                </Typography>
              </Box>
            </Box>
            <IconButton
              size="small"
              onClick={() => setIsOpen(false)}
              aria-label="Закрыть"
              sx={{ color: 'white', minWidth: 44, minHeight: 44 }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Body */}
          <Box sx={{ p: 2, bgcolor: '#ECE5DD' }}>
            {/* Welcome message bubble */}
            <Box
              sx={{
                bgcolor: 'white',
                p: 1.5,
                borderRadius: 2,
                borderTopLeftRadius: 0,
                maxWidth: '85%',
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
              }}
            >
              <Typography variant="body2">
                👋 Привет! Чем можем помочь?
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.5, textAlign: 'right' }}
              >
                WASSER
              </Typography>
            </Box>
          </Box>

          {/* Input */}
          <Box sx={{ p: 2, bgcolor: '#f0f0f0', display: 'flex', gap: 1 }}>
            <TextField
              id="whatsapp-message"
              name="message"
              autoComplete="off"
              fullWidth
              size="small"
              placeholder="Введите сообщение..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleWhatsAppClick()}
              sx={{
                bgcolor: 'white',
                borderRadius: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
            <IconButton
              onClick={handleWhatsAppClick}
              aria-label="Отправить"
              sx={{
                bgcolor: '#25D366',
                color: 'white',
                minWidth: 44,
                minHeight: 44,
                '&:hover': { bgcolor: '#128C7E' },
              }}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </Paper>
      </Zoom>

      {/* Floating button */}
      <Tooltip title="Написать в WhatsApp" placement="left" arrow>
        <Fab
          onClick={() => setIsOpen(!isOpen)}
          sx={{
            position: 'fixed',
            bottom: { xs: 140, sm: 24 }, // Above mobile nav + chat button on xs
            right: { xs: 16, sm: 24 },
            bgcolor: '#25D366',
            color: 'white',
            width: 60,
            height: 60,
            boxShadow: '0 4px 20px rgba(37, 211, 102, 0.4)',
            '&:hover': {
              bgcolor: '#128C7E',
              transform: 'scale(1.1)',
            },
            transition: 'all 0.3s ease',
            zIndex: 1300,
            // Pulse animation
            animation: !isOpen ? 'pulse 2s infinite' : 'none',
            '@keyframes pulse': {
              '0%': { boxShadow: '0 0 0 0 rgba(37, 211, 102, 0.7)' },
              '70%': { boxShadow: '0 0 0 15px rgba(37, 211, 102, 0)' },
              '100%': { boxShadow: '0 0 0 0 rgba(37, 211, 102, 0)' },
            },
          }}
        >
          {isOpen ? (
            <CloseIcon sx={{ fontSize: 28 }} />
          ) : (
            <WhatsAppIcon sx={{ fontSize: 32 }} />
          )}
        </Fab>
      </Tooltip>

      {/* Quick action buttons (show when closed) */}
      {!isOpen && (
        <Zoom in={!isOpen} style={{ transitionDelay: '100ms' }}>
          <Box
            sx={{
              position: 'fixed',
              bottom: { xs: 206, sm: 90 }, // Above WhatsApp button
              right: { xs: 20, sm: 28 },
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              zIndex: 1299,
            }}
          >
            <Tooltip title="Быстрый вопрос" placement="left" arrow>
              <Button
                variant="contained"
                size="small"
                onClick={handleQuickContact}
                sx={{
                  bgcolor: 'white',
                  color: '#25D366',
                  borderRadius: 3,
                  px: 2,
                  py: 0.75,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  boxShadow: 2,
                  '&:hover': { bgcolor: '#f5f5f5' },
                  whiteSpace: 'nowrap',
                }}
              >
                💬 Есть вопрос?
              </Button>
            </Tooltip>
          </Box>
        </Zoom>
      )}
    </>
  );
};

export default WhatsAppButton;
